import { useState } from "react";

import {
  faExpand,
  faPlay,
  faPlus,
  faTrashAlt,
} from "@fortawesome/pro-regular-svg-icons";
import { createPortal } from "react-dom";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { useValidateCustomAuth } from "src/api/connectApi/queries";
import { SampleRequestConfig } from "src/api/connectApi/types";
import { useWorkspace } from "src/api/queries";
import { Button } from "src/base-components/Button";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";
import { FloatingWindowInternal } from "src/base-components/FloatingWindow/FloatingWindow";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { ConfigFieldsetCard as FieldsetCard } from "src/connections/config/FieldsetCard";
import { KeyValuePairField } from "src/connections/config/KeyValuePairField";
import {
  AvailableEnvironmentPrefixes,
  ConnectionConfigInputs,
} from "src/connections/types";
import { Accordion } from "src/design-system/Accordion";
import { Callout } from "src/design-system/Callout";
import { IconButton } from "src/design-system/Icon";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type Props = {
  environmentPrefix: AvailableEnvironmentPrefixes;
};

const HTTP_METHODS: Array<{ key: string; value: string }> = [
  { key: "GET", value: "GET" },
  { key: "POST", value: "POST" },
  { key: "PUT", value: "PUT" },
  { key: "PATCH", value: "PATCH" },
  { key: "DELETE", value: "DELETE" },
];

const DEFAULT_SAMPLE_REQUEST: Omit<SampleRequestConfig, "headers"> = {
  method: "POST",
  url: "http://localhost/sample-endpoint",
  body: '{"message": "Hello, world!"}',
};

type HeaderEntry = { key: string; value: string };

const DEFAULT_HEADERS: HeaderEntry[] = [
  { key: "Content-Type", value: "application/json" },
];

const entriesToHeaders = (entries: HeaderEntry[]): Record<string, string> =>
  Object.fromEntries(
    entries
      .filter((e) => e.key.trim())
      .map((e) => [e.key.trim(), e.value] as const),
  );

type ExpandableCodeEditorProps = {
  dataLoc: string;
  language: "python" | "json";
  placeholder: string;
  value: string;
  title: string;
  expandedWidth: `w-${number}`;
  expandedHeight: `h-${number}`;
  height?: `h-${number}`;
  onChange: (value: string | undefined) => void;
  onBlur?: () => void;
};

const ExpandableCodeEditor: React.FC<ExpandableCodeEditorProps> = ({
  dataLoc,
  language,
  placeholder,
  value,
  title,
  expandedWidth,
  expandedHeight,
  height,
  onChange,
  onBlur,
}) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="relative">
      <div className="absolute top-1 right-1 z-10">
        <IconButton
          icon={faExpand}
          size="2xs"
          onClick={() => setExpanded(true)}
        />
      </div>
      <div className={height}>
        <CodeEditor
          dataLoc={dataLoc}
          highlightOnFocus
          language={language}
          placeholder={placeholder}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
        />
      </div>
      {expanded &&
        createPortal(
          <FloatingWindowInternal
            draggable={false}
            height={expandedHeight}
            isPinned={false}
            maximizable={true}
            style={{
              inset: "0px auto auto 0px",
              transform: "translate3d(calc(50vw - 50%), calc(50vh - 50%), 0)",
            }}
            title={title}
            width={expandedWidth}
            onClose={() => setExpanded(false)}
          >
            <div className="h-full">
              <CodeEditor
                backgroundColor="white"
                basicSetup={{
                  lineNumbers: true,
                  highlightActiveLine: true,
                }}
                dataLoc={`${dataLoc}-expanded`}
                language={language}
                placeholder={placeholder}
                value={value}
                highlightOnFocus
                lineWrapping
                onBlur={onBlur}
                onChange={onChange}
              />
            </div>
          </FloatingWindowInternal>,
          document.body,
        )}
    </div>
  );
};

type ValidationResultProps = {
  validateMutation: ReturnType<typeof useValidateCustomAuth>;
};

const ValidationResult: React.FC<ValidationResultProps> = ({
  validateMutation,
}) => {
  if (validateMutation.error) {
    return (
      <Callout variant="error">
        Request failed: {validateMutation.error.message}
      </Callout>
    );
  }

  const data = validateMutation.data;
  if (!data) return null;

  if (!data.success) {
    return (
      <Callout variant="error">
        {data.error?.message ?? "Validation failed"}
      </Callout>
    );
  }

  const raw = data.raw_request;
  const rawText = raw
    ? [
        `Request:`,
        `${raw.method.toUpperCase()} ${raw.url}`,
        `Headers:`,
        ...Object.entries(raw.headers).map(([k, v]) => `  ${k}: ${v}`),
        `Body:`,
        ...(raw.body ? [raw.body] : []),
      ].join("\n")
    : null;

  return (
    <Callout variant="success">
      <div className="flex flex-col gap-2">
        <span>
          Code is syntactically valid. Note that we didn't actually make the
          request to the provided endpoint but only generated the raw request.
        </span>
        {rawText && (
          <pre className="whitespace-pre-wrap font-mono text-xs">{rawText}</pre>
        )}
      </div>
    </Callout>
  );
};

export const CustomCodeFieldset: React.FC<Props> = ({ environmentPrefix }) => {
  const { control, getValues } = useFormContext<ConnectionConfigInputs>();

  const { wsId } = useParamsDecode<DashboardPageParams>();
  const workspace = useWorkspace(wsId);
  const baseUrl = workspace.data?.base_url;
  const validateMutation = useValidateCustomAuth(baseUrl);

  const authCode = useWatch({
    control,
    name: `${environmentPrefix}customCodeConfig.auth_code`,
  });
  const hasAuthCode = Boolean(authCode);

  const [sampleRequest, setSampleRequest] = useState<
    Omit<SampleRequestConfig, "headers">
  >(DEFAULT_SAMPLE_REQUEST);
  const [headerEntries, setHeaderEntries] =
    useState<HeaderEntry[]>(DEFAULT_HEADERS);

  const handleValidate = () => {
    const authCode = getValues(
      `${environmentPrefix}customCodeConfig.auth_code`,
    );
    if (!authCode) return;
    const currentSecrets = getValues(`${environmentPrefix}secrets`);
    const secretsDict: Record<string, string> = Object.fromEntries(
      (currentSecrets ?? []).flatMap((s) =>
        s.key && s.value ? [[s.key, s.value] as const] : [],
      ),
    );
    validateMutation.mutate({
      auth_code: authCode,
      secrets: secretsDict,
      sample_request: {
        ...sampleRequest,
        headers: entriesToHeaders(headerEntries),
      },
    });
  };

  return (
    <FloatingWindowsProvider>
      <FieldsetCard>
        <FormItem gap="sm">
          <KeyValuePairField
            cta="Add secret"
            name={`${environmentPrefix}secrets`}
            showHeader={false}
            isSensitive
          />
        </FormItem>
      </FieldsetCard>
      <FieldsetCard>
        <FormItem
          description="Write a Python function that authenticates the request. The function must be named 'authenticate' and accept (request, secrets) parameters."
          gap="sm"
          label="Authentication code"
          isRequired
        >
          <div className="mb-2">
            <Accordion dataLoc="sample-request-accordion">
              <Accordion.Item
                label="Configure sample request"
                value="sample-request"
              >
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <div className="w-28">
                      <FixedSimpleDropdown
                        buttonDataLoc="sample-request-method"
                        dataLoc="sample-request-method-dropdown"
                        elements={HTTP_METHODS}
                        placeholder="Method"
                        selectedKey={sampleRequest.method ?? "POST"}
                        onSelect={(method) =>
                          setSampleRequest((prev) => ({
                            ...prev,
                            method: method as SampleRequestConfig["method"],
                          }))
                        }
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        placeholder="http://localhost/sample-endpoint"
                        value={sampleRequest.url ?? ""}
                        fullWidth
                        onChange={(e) =>
                          setSampleRequest((prev) => ({
                            ...prev,
                            url: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <FormItem gap="xs" label="Headers">
                    <div className="flex flex-col items-start gap-1">
                      {headerEntries.map((entry, index) => (
                        <div
                          key={index}
                          className="flex w-full items-center gap-1"
                        >
                          <Input
                            placeholder="Key"
                            value={entry.key}
                            fullWidth
                            onChange={(e) =>
                              setHeaderEntries((prev) =>
                                prev.map((h, i) =>
                                  i === index
                                    ? { ...h, key: e.target.value }
                                    : h,
                                ),
                              )
                            }
                          />
                          <Input
                            placeholder="Value"
                            value={entry.value}
                            fullWidth
                            onChange={(e) =>
                              setHeaderEntries((prev) =>
                                prev.map((h, i) =>
                                  i === index
                                    ? { ...h, value: e.target.value }
                                    : h,
                                ),
                              )
                            }
                          />
                          <IconButton
                            icon={faTrashAlt}
                            onClick={() =>
                              setHeaderEntries((prev) => {
                                const next = prev.filter((_, i) => i !== index);
                                return next.length > 0
                                  ? next
                                  : [{ key: "", value: "" }];
                              })
                            }
                          />
                        </div>
                      ))}
                      <Button
                        iconLeft={faPlus}
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          setHeaderEntries((prev) => [
                            ...prev,
                            { key: "", value: "" },
                          ])
                        }
                      >
                        Add header
                      </Button>
                    </div>
                  </FormItem>
                  <FormItem gap="xs" label="Body">
                    <ExpandableCodeEditor
                      dataLoc="sample-request-body-editor"
                      expandedHeight="h-130"
                      expandedWidth="w-160"
                      language="json"
                      placeholder='{"message": "Hello, world!"}'
                      title="Request body"
                      value={sampleRequest.body ?? ""}
                      onChange={(value) =>
                        setSampleRequest((prev) => ({
                          ...prev,
                          body: value ?? "",
                        }))
                      }
                    />
                  </FormItem>
                </div>
              </Accordion.Item>
            </Accordion>
          </div>
          <Controller
            control={control}
            name={`${environmentPrefix}customCodeConfig.auth_code`}
            rules={{ required: "Authentication code is required" }}
            render={({ field }) => (
              <ExpandableCodeEditor
                dataLoc={`${environmentPrefix}custom-code-auth-editor`}
                expandedHeight="h-195"
                expandedWidth="w-250"
                height="h-96"
                language="python"
                placeholder="Write your authentication code here..."
                title="Authentication code"
                value={field.value ?? ""}
                onBlur={field.onBlur}
                onChange={field.onChange}
              />
            )}
          />
        </FormItem>
        <div className="mt-3 flex flex-col items-start gap-2">
          <Button
            dataLoc="validate-custom-auth-button"
            disabled={!hasAuthCode || !baseUrl}
            iconLeft={faPlay}
            loading={validateMutation.isPending}
            size="sm"
            variant="secondary"
            onClick={handleValidate}
          >
            Validate
          </Button>
          {!validateMutation.isPending && (
            <ValidationResult validateMutation={validateMutation} />
          )}
        </div>
      </FieldsetCard>
    </FloatingWindowsProvider>
  );
};
